import React, { useState } from 'react';
import ModuleFrame, { Chip } from './ModuleFrame';
import ScreenReview from './lab/ScreenReview';
import { useEntityList } from '../data/entities';

// The accepted concept already holds the screens. Design (and the other
// stages that are about the product) draw those screens here through
// ScreenReview — click a screen to focus it, with its purpose and layout
// spelled out. The Lab remains the place you revise them;
// `onOpen(screenId, { revise })` jumps there with the same screen focused
// (and the revision panel open when a change was requested).

export function useAppConcept(orgId, appId) {
  const { docs } = useEntityList('labArtifact', { org: orgId }, { enabled: !!orgId && !!appId });
  if (!docs) return { loading: true, concept: null, stories: [] };
  const mine = docs.filter((a) => a.appId === appId);
  const concepts = mine.filter((a) => a.kind === 'concept').sort((a, b) => (b.version || 0) - (a.version || 0));
  const concept = concepts.find((a) => a.status === 'accepted') || concepts[0] || null;
  const stories = mine.filter((a) => a.kind === 'story').sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  return { loading: false, concept, stories };
}

function screenList(concept) {
  return concept?.spec?.screens || [];
}

export default function ConceptScreens({ org, appId, onOpen, variant = 'board', canRevise = false }) {
  const { loading, concept, stories } = useAppConcept(org?.id, appId);
  const [selected, setSelected] = useState(null);
  if (loading) return null;
  const screens = screenList(concept);
  const spec = concept?.spec || {};
  const open = onOpen ? (
    <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onOpen(selected)}>Open in the Lab</button>
  ) : null;
  // "Request a change" on a focused screen → the Lab's concept board with
  // that screen focused and the revision panel open (?revise=1). Only for
  // seats that may start Lab runs (`canRevise` = lab.run).
  const review = (compact) => <ScreenReview org={org} concept={concept} selected={selected} onSelect={setSelected} compact={compact} gallery onRequestChange={onOpen && canRevise ? (s) => onOpen(s.id, { revise: true }) : null} />;

  if (variant === 'board') {
    if (!concept) {
      return (
        <ModuleFrame.Panel title="Screens" meta="nothing drawn yet" actions={open}>
          <p className="cs-help" style={{ margin: 0 }}>No screens yet. The Lab draws them from the conversation about what this app is.</p>
        </ModuleFrame.Panel>
      );
    }
    const personas = (spec.personas || []).map((p) => p.name).filter(Boolean);
    const musts = (spec.features || []).filter((f) => f.priority === 'must').map((f) => f.name).filter(Boolean);
    return (
      <ModuleFrame.Panel
        title="Screens"
        meta={`concept v${concept.version || 1}${concept.status === 'accepted' ? ' · accepted' : ` · ${concept.status || 'draft'}`}`}
        actions={open}
      >
        {spec.summary?.problem && <p className="cs-help" style={{ marginTop: 0 }}>{spec.summary.problem}</p>}
        {(personas.length > 0 || musts.length > 0) && (
          <div className="ab-screen-facts">
            {personas.map((n) => <Chip key={n}>{n}</Chip>)}
            {musts.map((n) => <Chip key={n} tone="ok">{n}</Chip>)}
          </div>
        )}
        {review(false)}
      </ModuleFrame.Panel>
    );
  }

  if (!screens.length && !(variant === 'ship' && stories.length)) return null;
  return (
    <ModuleFrame.Panel
      title={variant === 'ship' ? 'What the gates are for' : 'Screens'}
      meta={concept ? `concept v${concept.version || 1}${screens.length ? ` · ${screens.length} screen${screens.length === 1 ? '' : 's'}` : ''}` : ''}
      actions={open}
    >
      {screens.length > 0 && review(true)}
      {variant === 'ship' && stories.length > 0 && (
        <ul className="mf-list" style={{ marginTop: screens.length ? 12 : 0 }}>
          {stories.slice(0, 8).map((s) => (
            <li key={s.id} className="mf-row">
              <Chip tone={s.status === 'verified' ? 'ok' : s.links?.lastVerdict === 'fail' ? 'danger' : 'muted'}>{s.status || 'story'}</Chip>
              <span className="mf-row-grow">{s.title || 'Untitled story'}</span>
            </li>
          ))}
          {stories.length > 8 && <li className="mf-row"><span className="mf-muted">{stories.length - 8} more on the Lab stories board</span></li>}
        </ul>
      )}
    </ModuleFrame.Panel>
  );
}